/**
 * Turning a dashboard API failure into something a user can read.
 *
 * Every dashboard call is a stored-procedure round-trip, and a SP failure comes
 * back as the whole statement that was run —
 * `EXEC [DayLight].[GetMetricValuesForDashboard] @CustomerSystemID = '…', … -
 * <reason>` — which is both unreadable and leaky in the UI. Hooks run the
 * response message through here before storing or toasting it, so panels show
 * the reason alone and expected empty states never surface as errors.
 */

/** A dashboard whose metrics are all disabled: an empty state, not a failure. */
const NO_ENABLED_METRICS = /no enabled metrics were found/i;

export const NO_METRICS_MESSAGE = "No metrics available for this dashboard";

export interface ApiErrorInfo {
  /** What to render — the reason only, never the raw statement. */
  message: string;
  /** False for expected empty states, which get the inline message and no toast. */
  toast: boolean;
}

/** The reason a SP error carries, with the `EXEC …` statement stripped off.
 *  Null when nothing readable is left. */
function readableReason(text: string): string | null {
  if (!text) return null;
  if (!/^EXEC\b/i.test(text)) return text;
  // The statement is prefixed onto the reason as `<sql> - <reason>`.
  const cut = text.lastIndexOf(" - ");
  const reason = cut === -1 ? "" : text.slice(cut + 3).trim();
  return reason || null;
}

/**
 * Maps a failed response's `message` to what the UI should do with it.
 * `fallback` is used when the server sent nothing usable.
 */
export function apiErrorInfo(
  raw: string | undefined | null,
  fallback: string,
): ApiErrorInfo {
  const text = (raw ?? "").trim();
  if (NO_ENABLED_METRICS.test(text)) {
    return { message: NO_METRICS_MESSAGE, toast: false };
  }
  return { message: readableReason(text) ?? fallback, toast: true };
}

/** `apiErrorInfo` for the hooks that only render the message inline. */
export function apiErrorMessage(
  raw: string | undefined | null,
  fallback: string,
): string {
  return apiErrorInfo(raw, fallback).message;
}
